import * as React from "react";
import {
  SimpleForm,
  required,
  TextInput,
  SelectInput,
  RecordContextProvider,
  Confirm,
  useNotify,
  useUpdate,
  useRedirect,
  useGetOne,
  useRefresh,
  AutocompleteInput,
  ReferenceInput,
} from "react-admin";
import moment from "moment";
import {
  Paper,
  Typography,
  IconButton,
  Grid,
  DialogActions,
  DialogContent,
  DialogTitle,
  Dialog,
  Button,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";
import CloseIcon from "@mui/icons-material/Close";
import { DateTimePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import {
  mapToOptions,
  statusOptions,
  poolOptions,
  autoRenewalOptions,
} from "./__variables/variables";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const useStyles = makeStyles({
  input: {
    marginBottom: "-20px",
  },
  toolbar: {
    display: "flex",
    justifyContent: "space-between",
  },
});

export interface DialogTitleProps {
  id: string;
  children?: React.ReactNode;
  onClose: () => void;
}

export interface UpdateDialogProps {
  openUpdateDialog: boolean;
  recordId: string;
  handleCloseUpdateDialog: () => void;
}

const BootstrapDialogTitle = (props: DialogTitleProps) => {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
};

export interface UpdatedDataProps {
  country: string;
  hkd_price: number | null;
  msisdn: string;
  user_id: string;
  map_to: string;
  status: string;
  is_pool: boolean;
  is_auto_renewed: boolean;
  allocation_date: string | Date | null;
  expiry_date: string | Date | null;
  free_date: string | Date | null;
}

const ConfirmDialogContent = ({ msisdn }: { msisdn: string }) => {
  return (
    <Paper elevation={0}>
      <Typography variant="body1" gutterBottom>
        {`Are you sure you want to update number ${msisdn}?`}
      </Typography>
    </Paper>
  );
};

export default function SFVirtualNumberUpdateDialog(props: UpdateDialogProps) {
  const { recordId, handleCloseUpdateDialog, openUpdateDialog } = props;
  // --
  const classes = useStyles();
  const refresh = useRefresh();
  const redirect = useRedirect();
  const notify = useNotify();
  // --
  const [updateConfirmOpen, setUpdateConfirmOpen] =
    React.useState<boolean>(false);

  const [updatedData, setUpdatedData] = React.useState<UpdatedDataProps>({
    country: "",
    hkd_price: null,
    msisdn: "",
    user_id: "",
    map_to: "",
    status: "",
    is_pool: false,
    is_auto_renewed: false,
    allocation_date: "",
    expiry_date: "",
    free_date: "",
  });
  // --
  const handleOpenUpdateConfirm = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    setUpdateConfirmOpen(true);
  };
  const handleCloseUpdateConfirm = () => {
    setUpdateConfirmOpen(false);
  };
  // --
  const ToolbarEdit = () => {
    return <></>;
  };
  // --
  const { data, isLoading: useGetOneLoading } = useGetOne(
    "smartfrenVirtualMsisdn",
    { id: recordId },
    { onError: () => redirect("/smartfrenVirtualMsisdn") }
  );
  // --
  React.useEffect(() => {
    setUpdatedData({
      country: data?.country,
      hkd_price: data?.hkd_price,
      msisdn: data?.msisdn,
      user_id: data?.user_id,
      map_to: data?.map_to,
      status: data?.status,
      is_pool: data?.is_pool,
      is_auto_renewed: data?.is_auto_renewed,
      allocation_date: data?.allocation_date,
      expiry_date: data?.expiry_date,
      free_date: data?.free_date,
    });
  }, [data]);
  // --
  const [update, { isLoading: isLoadingUpdate, isSuccess: isUpdateSuccess }] =
    useUpdate("smartfrenVirtualMsisdn", {
      id: recordId,
      data: { ...updatedData },
      previousData: { id: recordId, ...updatedData },
    });
  //--
  const handleUpdateRecord = () => {
    update();
  };
  // --
  React.useEffect(() => {
    if (isUpdateSuccess) {
      handleCloseUpdateConfirm();
      refresh();
      handleCloseUpdateDialog();
      notify("Msisdn Successfully Updated", { type: "success" });
    }
  }, [isUpdateSuccess]);
  // --
  if (useGetOneLoading) {
    return <>Loading . . .</>;
  }
  return (
    <RecordContextProvider value={data}>
      <LocalizationProvider
        dateAdapter={AdapterMoment}
        timezone="asia/hong_kong"
      >
        <div>
          <BootstrapDialog
            onClose={handleCloseUpdateDialog}
            aria-labelledby="customized-dialog-title"
            open={openUpdateDialog}
            fullWidth
          >
            <BootstrapDialogTitle
              id="customized-dialog-title"
              onClose={handleCloseUpdateDialog}
            >
              Update Smartfren Virtual Msisdn
            </BootstrapDialogTitle>
            <DialogContent dividers>
              <SimpleForm toolbar={<ToolbarEdit />} title=" ">
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <ReferenceInput
                      key="user_id"
                      source="user_id"
                      reference="subscribers"
                      label="Subscriber Name"
                    >
                      <AutocompleteInput
                        fullWidth
                        className={classes.input}
                        source="name"
                        data-testid="search-subscriber"
                        optionText={(choice) =>
                          `${choice.name} (${choice.phone})`
                        }
                        onChange={(value) =>
                          setUpdatedData((prevState: any) => ({
                            ...prevState,
                            user_id: value,
                          }))
                        }
                        label="Type in a name"
                      />
                    </ReferenceInput>
                  </Grid>
                  <Grid item xs={6}>
                    <TextInput
                      fullWidth
                      disabled
                      className={classes.input}
                      source="msisdn"
                      data-testid="msisdnInput"
                      label="msisdn"
                      validate={required()}
                      onChange={(e) =>
                        setUpdatedData((prevState: any) => ({
                          ...prevState,
                          msisdn: e.target.value,
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <SelectInput
                      fullWidth
                      className={classes.input}
                      source="map_to"
                      validate={[required()]}
                      choices={mapToOptions}
                      label="Map To"
                      data-test-id="maptop"
                      onChange={(e) =>
                        setUpdatedData((prevState: any) => ({
                          ...prevState,
                          map_to: e.target.value,
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <SelectInput
                      fullWidth
                      className={classes.input}
                      source="status"
                      validate={[required()]}
                      choices={statusOptions}
                      label="Status"
                      data-test-id="status"
                      onChange={(e) =>
                        setUpdatedData((prevState: any) => ({
                          ...prevState,
                          status: e.target.value,
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <SelectInput
                      fullWidth
                      className={classes.input}
                      source="is_pool"
                      validate={[required()]}
                      choices={poolOptions}
                      label="Pool"
                      data-test-id="pool"
                      onChange={(e) =>
                        setUpdatedData((prevState: any) => ({
                          ...prevState,
                          is_pool: e.target.value,
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <SelectInput
                      fullWidth
                      className={classes.input}
                      source="is_auto_renewed"
                      validate={[required()]}
                      choices={autoRenewalOptions}
                      label="Auto Renewal"
                      data-test-id="auto_renewal"
                      onChange={(e) =>
                        setUpdatedData((prevState: any) => ({
                          ...prevState,
                          is_auto_renewed: e.target.value,
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <DateTimePicker
                      label="Activation Date"
                      value={
                        updatedData.allocation_date
                          ? moment.utc(updatedData.allocation_date)
                          : null
                      }
                      onChange={(value: any) =>
                        setUpdatedData((prevState: any) => ({
                          ...prevState,
                          allocation_date: value
                            ? moment.utc(value).toISOString()
                            : null,
                        }))
                      }
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          className: classes.input,
                        },
                        actionBar: {
                          actions: ["accept", "cancel"],
                        },
                      }}
                      ampm={false}
                      format="DD/MM/YYYY HH:mm"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <DateTimePicker
                      label="Expire Date"
                      value={
                        updatedData.expiry_date
                          ? moment.utc(updatedData.expiry_date)
                          : null
                      }
                      onChange={(value: any) =>
                        setUpdatedData((prevState: any) => ({
                          ...prevState,
                          expiry_date: value
                            ? moment.utc(value).toISOString()
                            : null,
                        }))
                      }
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          className: classes.input,
                        },
                        actionBar: {
                          actions: ["accept", "cancel"],
                        },
                      }}
                      ampm={false}
                      format="DD/MM/YYYY HH:mm"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <DateTimePicker
                      label="Free Date"
                      value={
                        updatedData.free_date
                          ? moment.utc(updatedData.free_date)
                          : null
                      }
                      onChange={(value: any) =>
                        setUpdatedData((prevState: any) => ({
                          ...prevState,
                          free_date: value
                            ? moment.utc(value).toISOString()
                            : null,
                        }))
                      }
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          className: classes.input,
                        },
                        actionBar: {
                          actions: ["accept", "cancel"],
                        },
                      }}
                      ampm={false}
                      format="DD/MM/YYYY HH:mm"
                    />
                  </Grid>
                </Grid>
              </SimpleForm>
            </DialogContent>
            <DialogActions>
              <Button data-testid="save-btn" onClick={handleOpenUpdateConfirm}>
                Save
              </Button>
            </DialogActions>
          </BootstrapDialog>
        </div>
        <Confirm
          isOpen={updateConfirmOpen}
          title="Dear User,"
          content={<ConfirmDialogContent msisdn={data?.msisdn} />}
          onClose={handleCloseUpdateConfirm}
          confirm="Proceed"
          onConfirm={handleUpdateRecord}
        />
      </LocalizationProvider>
    </RecordContextProvider>
  );
}
